import { z } from 'zod'
import type { AiActionKey } from './ai-credits.ts'

/**
 * Sermon writing ideas (docs/15-MUSIC-AND-AI.md, "Sermon writing"): what a
 * model writes from a plan's read sermon — a small-group guide, a recap for
 * the newsletter and social media, a family devotional and kids' version,
 * words for the service's flow, a slide deck — and, church-wide, a series
 * outline. Each is kept (`sermon_ideas`), shown as a suggestion, editable
 * before anyone copies or uses it, and applied only when a person chooses.
 *
 * The shapes here are the one truth for both sides: the API reads a
 * model's answer and a person's edit through them, the web edits them.
 */

// ---- kinds ------------------------------------------------------------------------------

/** What can be written from a plan's sermon. */
export const PLAN_IDEA_KINDS = [
  'group_guide',
  'recap',
  'family',
  'service_flow',
  'slide_deck',
] as const
export type PlanIdeaKind = (typeof PLAN_IDEA_KINDS)[number]

/** Every kind, the church-level series outline too. */
export const IDEA_KINDS = [...PLAN_IDEA_KINDS, 'series_plan'] as const
export type IdeaKind = (typeof IDEA_KINDS)[number]

export const isPlanIdeaKind = (value: string): value is PlanIdeaKind =>
  (PLAN_IDEA_KINDS as readonly string[]).includes(value)

/** `processing` (queued or being written), `ready`, `failed` (with why). */
export const IDEA_STATES = ['processing', 'ready', 'failed'] as const
export type IdeaState = (typeof IDEA_STATES)[number]

/** The AI action (and so the credits) each kind is charged as (ai-credits.ts). */
export const IDEA_ACTIONS = {
  group_guide: 'group_guide',
  recap: 'sermon_recap',
  family: 'family_devotional',
  service_flow: 'service_flow',
  slide_deck: 'slide_deck',
  series_plan: 'series_plan',
} as const satisfies Record<IdeaKind, AiActionKey>

/** What each kind is called, and what it gives, for its card. */
export const IDEA_LABELS: Record<IdeaKind, { title: string; blurb: string }> = {
  group_guide: {
    title: 'Small-group guide',
    blurb:
      'An opening question, the passages, discussion questions, a step for the week, a prayer.',
  },
  recap: {
    title: 'Recap for email and social',
    blurb: 'A newsletter paragraph, three captions of different lengths, a pull quote.',
  },
  family: {
    title: 'Kids & family',
    blurb: 'Five short days of family devotions, and the sermon explained for children.',
  },
  service_flow: {
    title: 'Service flow writing',
    blurb: 'A call to worship, prayers, transitions and a benediction, each by its item.',
  },
  slide_deck: {
    title: 'Slide deck',
    blurb:
      'The key points and scripture references as slides, to download for PowerPoint or Keynote.',
  },
  series_plan: {
    title: 'Series outline',
    blurb: 'Names for the series, and each week’s title, passage and big idea.',
  },
}

// ---- the shapes -----------------------------------------------------------------------

const line = (max: number) => z.string().trim().min(1, 'Can’t be empty').max(max)
const optional = (max: number) => z.string().trim().max(max)

export const groupGuideSchema = z.object({
  title: line(200),
  openingQuestion: line(1000),
  passages: z
    .array(z.object({ reference: line(80), note: optional(600) }))
    .min(1)
    .max(8),
  questions: z.array(line(600)).min(1).max(10),
  thisWeek: line(1000),
  closingPrayer: line(1500),
})
export type GroupGuide = z.infer<typeof groupGuideSchema>

export const CAPTION_LENGTHS = ['short', 'medium', 'long'] as const
export type CaptionLength = (typeof CAPTION_LENGTHS)[number]

export const recapSchema = z.object({
  newsletter: line(3000),
  captions: z
    .array(z.object({ length: z.enum(CAPTION_LENGTHS), text: line(2200) }))
    .min(1)
    .max(5),
  pullQuote: line(400),
})
export type Recap = z.infer<typeof recapSchema>

export const familySchema = z.object({
  days: z
    .array(
      z.object({
        title: line(120),
        verse: line(80),
        thought: line(1200),
        question: line(600),
        prayer: line(800),
      }),
    )
    .min(1)
    .max(7),
  kids: z.object({ title: line(200), explanation: line(2500) }),
})
export type FamilyDevotional = z.infer<typeof familySchema>

/** The pieces a service's flow is written in, in the order a service usually has them. */
export const FLOW_PIECE_KINDS = [
  'call_to_worship',
  'opening_prayer',
  'transition',
  'prayer_of_response',
  'benediction',
] as const
export type FlowPieceKind = (typeof FLOW_PIECE_KINDS)[number]

export const FLOW_PIECE_LABELS: Record<FlowPieceKind, string> = {
  call_to_worship: 'Call to worship',
  opening_prayer: 'Opening prayer',
  transition: 'Transition',
  prayer_of_response: 'Prayer of response',
  benediction: 'Benediction',
}

export const flowPieceSchema = z.object({
  /** Stable within its idea: Apply and Dismiss name it. */
  id: line(40),
  kind: z.enum(FLOW_PIECE_KINDS),
  /** The running-order item it belongs with; null when that item is gone since. */
  itemId: z.string().nullable(),
  /** The item's title when it was written (to say which it was once it is gone). */
  itemTitle: optional(200),
  text: line(1500),
  /** Written into the item's description: when. */
  appliedAt: z.string().nullable(),
  dismissedAt: z.string().nullable(),
})
export type FlowPiece = z.infer<typeof flowPieceSchema>

export const serviceFlowSchema = z.object({ pieces: z.array(flowPieceSchema).max(24) })
export type ServiceFlow = z.infer<typeof serviceFlowSchema>

export const SLIDE_KINDS = ['title', 'point', 'scripture', 'close'] as const
export type SlideKind = (typeof SLIDE_KINDS)[number]

export const slideSchema = z.object({
  kind: z.enum(SLIDE_KINDS),
  heading: line(200),
  /** Bullet lines (a point's); none on a scripture slide. */
  lines: z.array(line(300)).max(6),
  /** The passage a scripture slide shows ("John 1:14"); the words are the church's translation's. */
  reference: optional(80).nullable(),
})
export type Slide = z.infer<typeof slideSchema>

/**
 * A deck's look when its series has no slide background: a quiet navy, the
 * words white — legible on any projector. The .pptx (lib/pptx.ts) and the
 * preview on screen read the same colours (hex, no #).
 */
export const DECK_THEME = {
  background: ['1E293B', '0F172A'],
  text: 'FFFFFF',
  accent: '93C5FD',
} as const

export const slideDeckSchema = z.object({
  title: line(200),
  slides: z.array(slideSchema).min(1).max(30),
})
export type SlideDeck = z.infer<typeof slideDeckSchema>

export const seriesPlanSchema = z.object({
  names: z.array(line(120)).min(1).max(5),
  description: line(1500),
  weeks: z
    .array(
      z.object({
        title: line(200),
        passage: line(80),
        bigIdea: line(400),
        summary: line(600),
      }),
    )
    .min(1)
    .max(12),
})
export type SeriesPlan = z.infer<typeof seriesPlanSchema>

/** What the series planner is asked: a book or a topic, how long, for whom. */
export const SERIES_WEEKS_MIN = 4
export const SERIES_WEEKS_MAX = 8
export const SERIES_AUDIENCES = [
  'The whole church',
  'Students',
  'Young adults',
  'Families with children',
  'New believers',
] as const

export const seriesPlanInputSchema = z
  .object({
    source: z.enum(['book', 'topic']),
    book: z.string().trim().max(40),
    topic: z.string().trim().max(200),
    weeks: z.coerce
      .number<string | number>()
      .int()
      .min(SERIES_WEEKS_MIN, `At least ${SERIES_WEEKS_MIN} weeks`)
      .max(SERIES_WEEKS_MAX, `At most ${SERIES_WEEKS_MAX} weeks`),
    audience: z.string().trim().min(1, 'Choose who it is for').max(120),
    notes: z.string().trim().max(1000, 'At most 1,000 characters'),
  })
  .superRefine((value, ctx) => {
    if (value.source === 'book' && !value.book)
      ctx.addIssue({ code: 'custom', path: ['book'], message: 'Choose a book' })
    if (value.source === 'topic' && value.topic.length < 3)
      ctx.addIssue({ code: 'custom', path: ['topic'], message: 'Say what the series is about' })
  })
export type SeriesPlanInput = z.output<typeof seriesPlanInputSchema>
export type SeriesPlanFormInput = z.input<typeof seriesPlanInputSchema>

/** What each kind's content is. */
export interface IdeaContent {
  group_guide: GroupGuide
  recap: Recap
  family: FamilyDevotional
  service_flow: ServiceFlow
  slide_deck: SlideDeck
  series_plan: SeriesPlan
}

export const IDEA_SCHEMAS = {
  group_guide: groupGuideSchema,
  recap: recapSchema,
  family: familySchema,
  service_flow: serviceFlowSchema,
  slide_deck: slideDeckSchema,
  series_plan: seriesPlanSchema,
} as const satisfies { [K in IdeaKind]: z.ZodType<IdeaContent[K]> }

/** A stored (or edited) idea's content, read safely: null when it isn't that kind's shape. */
export function parseIdeaContent<K extends IdeaKind>(
  kind: K,
  value: unknown,
): IdeaContent[K] | null {
  const parsed = IDEA_SCHEMAS[kind].safeParse(value)
  return parsed.success ? (parsed.data as IdeaContent[K]) : null
}

// ---- service flow ------------------------------------------------------------------------

/**
 * A suggestion's words put into an item's description: in place of an
 * empty one, else after it, a blank line between; never past the
 * description's limit, and never twice.
 */
export const ITEM_DESCRIPTION_MAX = 2000
export function appendToDescription(current: string | null, text: string): string {
  const now = (current ?? '').trim()
  const add = text.trim()
  if (!now) return add.slice(0, ITEM_DESCRIPTION_MAX)
  if (now.includes(add)) return now
  return `${now}\n\n${add}`.slice(0, ITEM_DESCRIPTION_MAX)
}

/** The pieces still to decide on (not applied, not dismissed), for an item or all. */
export function openPieces(flow: ServiceFlow | null, itemId?: string): FlowPiece[] {
  return (flow?.pieces ?? []).filter(
    (p) =>
      p.appliedAt === null &&
      p.dismissedAt === null &&
      (itemId === undefined || p.itemId === itemId),
  )
}

// ---- as text, to copy -----------------------------------------------------------------

const block = (heading: string, body: string) => `${heading}\n${body}`

/** A small-group guide as plain text (headings, numbered questions), to paste anywhere. */
export function groupGuideText(guide: GroupGuide): string {
  return [
    guide.title,
    block('Opening question', guide.openingQuestion),
    block(
      'Read',
      guide.passages.map((p) => (p.note ? `${p.reference} — ${p.note}` : p.reference)).join('\n'),
    ),
    block('Discuss', guide.questions.map((q, i) => `${i + 1}. ${q}`).join('\n')),
    block('This week', guide.thisWeek),
    block('Close in prayer', guide.closingPrayer),
  ].join('\n\n')
}

export const CAPTION_LABELS: Record<CaptionLength, string> = {
  short: 'Short',
  medium: 'Medium',
  long: 'Long',
}

/** The whole recap as text: the newsletter paragraph, the captions, the quote. */
export function recapText(recap: Recap): string {
  return [
    block('Newsletter', recap.newsletter),
    ...recap.captions.map((c) => block(`${CAPTION_LABELS[c.length]} caption`, c.text)),
    block('Pull quote', `“${recap.pullQuote}”`),
  ].join('\n\n')
}

/** The family devotional and the kids' explanation as text. */
export function familyText(family: FamilyDevotional): string {
  return [
    block(family.kids.title, family.kids.explanation),
    ...family.days.map((day, i) =>
      [
        `Day ${i + 1}: ${day.title}`,
        `Read: ${day.verse}`,
        day.thought,
        `Talk about it: ${day.question}`,
        `Pray: ${day.prayer}`,
      ].join('\n'),
    ),
  ].join('\n\n')
}

/** A deck's outline as text: each slide's heading, its lines or reference. */
export function slideDeckText(deck: SlideDeck): string {
  return [
    deck.title,
    ...deck.slides.map((slide, i) =>
      [
        `${i + 1}. ${slide.heading}`,
        ...(slide.reference ? [`   ${slide.reference}`] : []),
        ...slide.lines.map((l) => `   • ${l}`),
      ].join('\n'),
    ),
  ].join('\n\n')
}

/** A series outline as text: its name ideas, then each week. */
export function seriesPlanText(plan: SeriesPlan): string {
  return [
    block('Name ideas', plan.names.map((n) => `• ${n}`).join('\n')),
    plan.description,
    ...plan.weeks.map((week, i) =>
      [
        `Week ${i + 1}: ${week.title}`,
        week.passage,
        `Big idea: ${week.bigIdea}`,
        week.summary,
      ].join('\n'),
    ),
  ].join('\n\n')
}

/** Every kind as text (Copy all). */
export function ideaText(kind: IdeaKind, content: IdeaContent[IdeaKind]): string {
  switch (kind) {
    case 'group_guide':
      return groupGuideText(content as GroupGuide)
    case 'recap':
      return recapText(content as Recap)
    case 'family':
      return familyText(content as FamilyDevotional)
    case 'slide_deck':
      return slideDeckText(content as SlideDeck)
    case 'series_plan':
      return seriesPlanText(content as SeriesPlan)
    default:
      return (content as ServiceFlow).pieces
        .map((p) => block(`${FLOW_PIECE_LABELS[p.kind]} · ${p.itemTitle}`, p.text))
        .join('\n\n')
  }
}

// ---- the series planner's weeks -----------------------------------------------------------

/** The Sundays (or whichever weekday it starts on) a series' weeks fall on: ISO days, a week apart. */
export function seriesWeekDates(startOn: string, weeks: number): string[] {
  const first = new Date(`${startOn}T12:00:00Z`)
  return Array.from({ length: weeks }, (_, i) => {
    const day = new Date(first.getTime() + i * 7 * 86_400_000)
    return day.toISOString().slice(0, 10)
  })
}

/** A file name from a title: "The Word Became Flesh" → "the-word-became-flesh". */
export function fileSlug(title: string): string {
  const slug = title
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60)
  return slug || 'sermon'
}
